import { useState, useLayoutEffect, useCallback, useEffect, useMemo } from "react";
import { ScrollView, Pressable, ActivityIndicator } from "react-native";
import { useRouter, useLocalSearchParams, Stack } from "expo-router";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { View, Text, ScreenContainer, FormInput } from "@src/components";
import { Icon } from "@src/components/Icon";
import { useAlarm, useUpdateAlarmTimezone } from "@src/services/api/hooks";
import { showSuccessToast, showErrorToast } from "@src/utils/toast";
import { UnistylesRuntime } from "react-native-unistyles";
import { useTenantId } from "@src/features/tenants";
import { useHeaderBackButton } from "@src/hooks/useHeaderBackButton";
import { isWeb } from "@src/utils/web-helpers";

// Australia and New Zealand timezones
function getAllTimezones(): string[] {
  return [
    "Australia/Adelaide",
    "Australia/Brisbane",
    "Australia/Broken_Hill",
    "Australia/Currie",
    "Australia/Darwin",
    "Australia/Eucla",
    "Australia/Hobart",
    "Australia/Lindeman",
    "Australia/Lord_Howe",
    "Australia/Melbourne",
    "Australia/Perth",
    "Australia/Sydney",
    "Pacific/Auckland",
    "Pacific/Chatham",
  ].sort();
}

export default function TimezoneScreen() {
  const router = useRouter();
  const theme = UnistylesRuntime.getTheme();
  const { bottom: safeAreaBottom } = useSafeAreaInsets();
  const tenantId = useTenantId();
  const { siteId, cameraKey } = useLocalSearchParams<{
    siteId: string;
    cameraKey: string;
  }>();
  const { data: alarm, isLoading: isLoadingAlarm } = useAlarm(siteId, cameraKey);
  const updateTimezoneMutation = useUpdateAlarmTimezone();
  const fallbackRoute = useMemo(() => {
    const encodedTenantId = encodeURIComponent(tenantId);
    const encodedSiteId = encodeURIComponent(siteId);
    const encodedCameraKey = encodeURIComponent(cameraKey);
    return `/tenants/${encodedTenantId}/sites/${encodedSiteId}/cameras/${encodedCameraKey}/alarm`;
  }, [tenantId, siteId, cameraKey]);
  const { headerLeft } = useHeaderBackButton(fallbackRoute);

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTimezone, setSelectedTimezone] = useState<string>("");

  // Update selected timezone when alarm data loads
  useEffect(() => {
    if (alarm?.settings?.timezone) {
      setSelectedTimezone(alarm.settings.timezone);
    }
  }, [alarm]);

  const allTimezones = useMemo(() => getAllTimezones(), []);

  // Filter timezones based on search query
  const filteredTimezones = useMemo(() => {
    if (!searchQuery.trim()) {
      return allTimezones;
    }
    const query = searchQuery.toLowerCase();
    return allTimezones.filter((tz) => tz.toLowerCase().includes(query));
  }, [allTimezones, searchQuery]);

  const handleSave = useCallback(() => {
    if (!cameraKey || !selectedTimezone) return;

    updateTimezoneMutation.mutate(
      { siteId, cameraKey, timezone: selectedTimezone },
      {
        onSuccess: () => {
          showSuccessToast({ message: "Timezone saved" });
          router.back();
        },
        onError: (error) => {
          showErrorToast({ message: "Error", description: "Failed to update alarm timezone. Please try again." });
          console.error("Failed to update alarm timezone:", error);
        },
      },
    );
  }, [cameraKey, siteId, selectedTimezone, updateTimezoneMutation, router]);

  if (isLoadingAlarm) {
    return (
      <ScreenContainer edges={[]}>
        <Stack.Screen
          options={{
            title: "Select Timezone",
            headerBackButtonDisplayMode: "minimal",
            headerLeft,
          }}
        />
        <View flex={1} justifyContent="center" alignItems="center">
          <ActivityIndicator size="large" />
        </View>
      </ScreenContainer>
    );
  }

  return (
    <ScreenContainer edges={[]}>
      <Stack.Screen
        options={{
          title: "Select Timezone",
          headerBackButtonDisplayMode: "minimal",
          headerLeft,
          headerRight: () => (
            <Pressable
              onPress={handleSave}
              style={{ paddingHorizontal: 16 }}
              disabled={updateTimezoneMutation.isPending || !selectedTimezone}
            >
              {updateTimezoneMutation.isPending ?
                <ActivityIndicator size="small" />
              : <Text textStyle="md/semibold" color={selectedTimezone ? "navy" : "textMuted"}>
                  Save
                </Text>
              }
            </Pressable>
          ),
        }}
      />
      <View flex={1}>
        {isWeb && (
          <View px="5xl" pt="4xl" pb="xl" bg="background" alignItems="flex-end">
            <Pressable
              onPress={handleSave}
              disabled={updateTimezoneMutation.isPending || !selectedTimezone}
              style={{ paddingHorizontal: 4 }}
            >
              {updateTimezoneMutation.isPending ?
                <ActivityIndicator size="small" />
              : <Text textStyle="md/semibold" color={selectedTimezone ? "navy" : "textMuted"}>
                  Save
                </Text>
              }
            </Pressable>
          </View>
        )}

        {/* Search Bar */}
        <View px="5xl" py="4xl" bg="background">
          <FormInput placeholder="Search timezones" value={searchQuery} onChangeText={setSearchQuery} size="medium" />
        </View>

        {/* Timezone List */}
        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={{ paddingBottom: safeAreaBottom + theme.spacing["5xl"] }}
        >
          <View px="5xl">
            {filteredTimezones.length === 0 ?
              <View py="4xl" alignItems="center">
                <Text color="textMuted" textStyle="sm/regular">
                  No timezones found
                </Text>
              </View>
            : filteredTimezones.map((timezone) => {
                const isSelected = selectedTimezone === timezone;
                return (
                  <Pressable
                    key={timezone}
                    onPress={() => setSelectedTimezone(timezone)}
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      paddingVertical: theme.spacing["3xl"],
                      paddingHorizontal: theme.spacing.xl,
                      borderBottomWidth: 1,
                      borderBottomColor: theme.colors.border,
                    }}
                  >
                    <Text textStyle="md/medium" style={{ flex: 1 }}>
                      {timezone}
                    </Text>
                    {isSelected && (
                      <Icon
                        name="checkmark"
                        size={theme.spacing["5xl"]}
                        color={theme.colors.interactive.buttons.default_background}
                      />
                    )}
                  </Pressable>
                );
              })
            }
          </View>
        </ScrollView>
      </View>
    </ScreenContainer>
  );
}
